import { Typography, type TypographyProps } from "@mui/material";
import type { ReactNode } from "react";

import { useInvocaNumber } from "@/lib/hooks/useInvocaNumber";
import { formatPhoneNumber } from "@/lib/utils/format-phone-number";

type Props = Omit<TypographyProps<"a", { component: "a" }>, "children" | "component" | "href"> & {
  phoneNumber: string | null | undefined;
  children?: ReactNode;
};

/**
 * Facility click-to-call link. Always carries the `InvocaNumber` class and triggers
 * an Invoca rescan on mount so the number is swapped for a tracking number.
 *
 * Keyed by number so a change (e.g. national fallback → office) mounts a fresh
 * anchor. Patching the old one would update React's text but leave Invoca's
 * swapped href in place, so the link would show one number and dial another.
 */
export default function PhoneLink({ phoneNumber, className, children, ...rest }: Readonly<Props>) {
  const { ref } = useInvocaNumber({ enabled: Boolean(phoneNumber) });

  if (!phoneNumber) return null;

  return (
    <Typography
      key={phoneNumber}
      {...rest}
      ref={ref}
      component="a"
      href={`tel:${phoneNumber}`}
      className={className ? `InvocaNumber ${className}` : "InvocaNumber"}
    >
      {children ?? formatPhoneNumber(phoneNumber) ?? phoneNumber}
    </Typography>
  );
}
